import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link } from "react-router-dom";
import {
  AlertTriangle, TruckIcon, Receipt, UserX, ShieldCheck, Users, Camera,
  Recycle, FileBarChart, ChevronRight, CalendarClock,
} from "lucide-react";

/**
 * Admin-only operations single-pane-of-glass.
 *  · Top: business signals (missed pickups, overdue invoices, absentees, expiring licences, pending data-capture)
 *  · Middle: this-month kg + top-earning clients
 *  · Bottom: client picker → drill-down (photos, streams, licences, pickups, invoices)
 */
export default function Operations() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [attendanceToday, setAttendanceToday] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientMap, setClientMap] = useState({});
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/ops/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/ops/attendance/today").then((r) => setAttendanceToday(r.data)).catch(() => {});
    api.get("/clients").then((r) => { setClients(r.data); setClientMap(Object.fromEntries(r.data.map((c) => [c.id, c.name]))); });
  }, [user]);

  useEffect(() => {
    if (!selectedClient) { setClientDetail(null); return; }
    api.get(`/ops/client/${selectedClient}`).then((r) => setClientDetail(r.data)).catch(() => {});
  }, [selectedClient]);

  if (user && user.role !== "admin") return <Navigate to="/app" replace />;

  const sig = overview?.signals || {};
  const s = clientDetail;

  const Card = ({ label, value, hint, icon: Icon, tone = "neutral", testid, to }) => {
    const body = (
      <div className="hbt-card p-5 h-full flex flex-col justify-between" data-testid={testid}>
        <div className="flex items-center justify-between mb-3">
          <div className="hbt-label">{label}</div>
          <Icon className="w-4 h-4" style={{ color: tone === "warn" ? "var(--terracotta,#C85A3C)" : tone === "good" ? "var(--forest)" : "#5B6663" }} />
        </div>
        <div className="font-display text-4xl font-semibold font-mono-num" style={{ color: tone === "warn" ? "var(--terracotta,#C85A3C)" : "inherit" }}>{value ?? "—"}</div>
        {hint && <div className="text-xs text-neutral-500 mt-2">{hint}</div>}
      </div>
    );
    return to ? <Link to={to} className="block hover:opacity-90 transition-opacity">{body}</Link> : body;
  };

  const onSite = attendanceToday.filter((a) => a.status === "on-site");
  const absent = attendanceToday.filter((a) => a.status === "absent");

  return (
    <div>
      <PageHeader eyebrow="Command centre" title="Operations" subtitle="Everything you need to run the business today — one glance." />
      <div className="px-8 lg:px-12 py-8 space-y-8">

        {/* Business signals */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ops-signals">
          <Card label="Missed pickups" value={sig.missed_pickups} icon={CalendarClock} tone={sig.missed_pickups > 0 ? "warn" : "good"} hint="Requested but not completed" testid="sig-missed" to="/app/pickups" />
          <Card label="Overdue invoices" value={(sig.overdue_invoices || []).length} icon={Receipt} tone={(sig.overdue_invoices || []).length > 0 ? "warn" : "good"} hint="Past due date, unpaid" testid="sig-overdue" to="/app/invoices" />
          <Card label="Pending data capture" value={sig.pending_data_capture} icon={TruckIcon} tone={sig.pending_data_capture > 0 ? "warn" : "good"} hint="Driver stops awaiting kg entry" testid="sig-pending-capture" to="/app/collections" />
          <Card label="Expiring licences" value={(sig.expiring_licenses || []).length} icon={ShieldCheck} tone={(sig.expiring_licenses || []).length > 0 ? "warn" : "good"} hint="Next 30 days" testid="sig-licenses" to="/app/licenses" />
          <Card label="Team on site" value={`${onSite.length}/${sig.active_internal_headcount || 0}`} icon={Users} tone="good" hint={`${absent.length} absent`} testid="sig-onsite" />
          <Card label="Silent 3+ days" value={sig.absentee_count} icon={UserX} tone={sig.absentee_count > 0 ? "warn" : "good"} hint="Haven't clocked in recently" testid="sig-absentees" />
          <Card label="This month" value={`${(overview?.this_month_kg || 0).toLocaleString()} kg`} icon={Recycle} tone="good" hint="Total waste processed" testid="sig-month-kg" />
          <Card label="Active clients" value={clients.length} icon={FileBarChart} tone="good" hint="On the books" testid="sig-clients" />
        </div>

        {/* Top clients + expiring detail */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="hbt-card p-5">
            <div className="hbt-label mb-3">Top clients this month</div>
            <div className="space-y-2">
              {(overview?.top_clients || []).map((row) => (
                <div key={row.client_id} className="flex items-center justify-between text-sm py-1 border-b" style={{ borderColor: "var(--border)" }}>
                  <span className="truncate">{clientMap[row.client_id] || "—"}</span>
                  <span className="font-mono-num font-semibold">{row.kg.toLocaleString()} kg</span>
                </div>
              ))}
              {(overview?.top_clients || []).length === 0 && <div className="text-xs text-neutral-500">No collections logged this month yet.</div>}
            </div>
          </div>
          <div className="hbt-card p-5">
            <div className="hbt-label mb-3 flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5" /> Attention needed</div>
            <div className="space-y-3 text-sm">
              {(sig.expiring_licenses || []).slice(0, 5).map((l) => (
                <div key={l.id} className="flex items-center justify-between">
                  <span className="truncate">{l.name} · {clientMap[l.client_id]}</span>
                  <span className="text-xs font-mono-num text-neutral-500">{l.expiry_date}</span>
                </div>
              ))}
              {(sig.overdue_invoices || []).slice(0, 5).map((i) => (
                <div key={i.id} className="flex items-center justify-between">
                  <span className="truncate">{i.invoice_number} · {clientMap[i.client_id]}</span>
                  <span className="text-xs font-mono-num" style={{ color: "var(--terracotta,#C85A3C)" }}>overdue</span>
                </div>
              ))}
              {(sig.expiring_licenses || []).length === 0 && (sig.overdue_invoices || []).length === 0 && (
                <div className="text-xs text-neutral-500">Nothing overdue — nice work.</div>
              )}
            </div>
          </div>
        </div>

        {/* Client drill-down */}
        <div>
          <div className="hbt-label mb-3">Client drill-down</div>
          <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4">
            <div className="hbt-card p-2 max-h-[520px] overflow-y-auto" data-testid="ops-client-list">
              {clients.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelectedClient(c.id)}
                  className="w-full text-left px-3 py-2 rounded hover:bg-neutral-50 flex items-center justify-between text-sm"
                  style={{ background: selectedClient === c.id ? "rgba(26,54,45,0.08)" : "transparent" }}
                  data-testid={`ops-client-${c.id}`}
                >
                  <span className="truncate">{c.name}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                </button>
              ))}
            </div>
            <div className="min-w-0">
              {!s && <div className="hbt-card p-8 text-center text-sm text-neutral-500">Pick a client to see their full operations.</div>}
              {s && (
                <div className="space-y-5" data-testid="ops-client-detail">
                  <div className="hbt-card p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="hbt-label">{s.client.industry || "Client"}</div>
                        <div className="font-display text-2xl font-semibold">{s.client.name}</div>
                        <div className="text-xs text-neutral-500 mt-1">{s.sites?.length || 0} site(s) · {s.licenses?.length || 0} licence(s)</div>
                      </div>
                      <div className="text-right">
                        <div className="hbt-label">This month</div>
                        <div className="font-display text-2xl font-semibold font-mono-num" style={{ color: "var(--forest)" }}>{(s.this_month?.total_kg || 0).toLocaleString()} kg</div>
                      </div>
                    </div>
                    {(s.this_month?.by_stream || []).length > 0 && (
                      <div className="mt-4 space-y-1.5">
                        {s.this_month.by_stream.slice(0, 5).map((r) => (
                          <div key={r.name} className="flex items-center justify-between text-xs">
                            <span className="truncate">{r.name}</span>
                            <span className="font-mono-num font-semibold">{r.kg.toLocaleString()} {r.unit}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {s.photos?.length > 0 && (
                    <div className="hbt-card p-5" data-testid="ops-client-photos">
                      <div className="hbt-label mb-3 flex items-center gap-2"><Camera className="w-3.5 h-3.5" /> Recent site photos</div>
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                        {s.photos.slice(0, 12).map((p, i) => (
                          <div key={i} className="relative aspect-square rounded overflow-hidden border" style={{ borderColor: "var(--border)" }}>
                            <img src={p.photo_after || p.photo_before} alt="" className="w-full h-full object-cover" />
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 py-0.5 font-mono-num truncate">{p.trip_date} · {p.driver}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="hbt-card p-5">
                      <div className="hbt-label mb-3">Latest collections</div>
                      {(s.collections || []).slice(0, 8).map((c) => (
                        <div key={c.id} className="text-xs py-1.5 flex justify-between border-b" style={{ borderColor: "var(--border)" }}>
                          <span className="truncate">{c.collection_date} · {c.waste_stream_name || c.waste_type}</span>
                          <span className="font-mono-num font-semibold">{(c.quantity ?? c.weight_kg ?? 0).toFixed(1)} {c.unit || "kg"}</span>
                        </div>
                      ))}
                      {(!s.collections || s.collections.length === 0) && <div className="text-xs text-neutral-500">No collections yet.</div>}
                    </div>
                    <div className="hbt-card p-5">
                      <div className="hbt-label mb-3">Recent pickups & invoices</div>
                      {(s.pickups || []).slice(0, 4).map((p) => (
                        <div key={p.id} className="text-xs py-1.5 flex justify-between border-b" style={{ borderColor: "var(--border)" }}>
                          <span className="truncate">Pickup · {p.requested_date}</span>
                          <span className="capitalize text-neutral-500">{p.status}</span>
                        </div>
                      ))}
                      {(s.invoices || []).slice(0, 4).map((i) => (
                        <div key={i.id} className="text-xs py-1.5 flex justify-between border-b" style={{ borderColor: "var(--border)" }}>
                          <span className="truncate">{i.invoice_number} · {i.issue_date}</span>
                          <span className="font-mono-num font-semibold">E{(i.total || 0).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
